import React, { useState, useCallback, useRef, useEffect } from "react";
import useApiEp from "../../hooks/useApiEp";
import axios from "axios";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Pagination,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import { getHeader } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";
import { ExportStatusBanner } from "../transactions/TransactionHistory";
/* ─── constants ─────────────────────────────────────────────── */
const PAGE_LIMIT = 20;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const POLL_INTERVAL_MS = 4000; // 4 seconds

const TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "usage", label: "Usage" },
  { value: "agent_notification", label: "Agent Notification" },
];

const STATUS_CONFIG = {
  Answered: { color: "success", icon: <PhoneIcon sx={{ fontSize: 12 }} /> },
  "Not Answered": {
    color: "error",
    icon: <PhoneDisabledIcon sx={{ fontSize: 12 }} />,
  },
  Busy: { color: "warning", icon: null },
  Failed: { color: "error", icon: null },
};

const TYPE_CHIP_CONFIG = {
  usage: { color: "primary" },
  agent_notification: { color: "secondary" },
};

const COLUMNS = [
  { label: "Customer ID", field: (r) => r.customerId, width: 160 },
  { label: "Type", field: (r) => r.type, width: 130, typeChip: true },
  {
    label: "Campaign ID",
    field: (r, p) => p.campaign_id,
    width: 110,
    mono: true,
  },
  {
    label: "Campaign Name",
    field: (r, p) => p.campaign_name,
    width: 200,
    truncate: true,
  },
  { label: "Mobile", field: (r, p) => p.mobile, width: 120, mono: true },
  { label: "CLI", field: (r, p) => p.cli, width: 120, mono: true },
  {
    label: "Answer Status",
    field: (r, p) => p.answer_status,
    width: 130,
    chip: true,
  },
  { label: "Duration", field: (r) => fmtDuration(r.durationSec), width: 90 },
  { label: "Billing (min)", field: (r, p) => p.billing_duration, width: 100 },
  { label: "Credits", field: (r) => r.creditsDebited, width: 80 },
  {
    label: "Hangup Cause",
    field: (r, p) => p.hangup_cause,
    width: 180,
    truncate: true,
  },
  { label: "Retry Count", field: (r, p) => p.retry_count, width: 90 },
  {
    label: "Answer Time",
    field: (r, p) => fmtDateTime(p.answer_time),
    width: 150,
  },
  {
    label: "Start Time",
    field: (r, p) => fmtDateTime(p.start_time),
    width: 150,
  },
  { label: "End Time", field: (r, p) => fmtDateTime(p.end_time), width: 150 },
  { label: "Created On", field: (r) => fmtDateTime(r.createdOn), width: 150 },
];

/* ─── helpers ───────────────────────────────────────────────── */
const fmtDuration = (sec) => {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60),
    s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const fmtDateTime = (v) => {
  if (!v) return "—";
  return new Date(v).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const buildQuery = (page, filters) => {
  const params = new URLSearchParams({ page, limit: PAGE_LIMIT });
  if (filters.name) params.set("name", filters.name);
  if (filters.campaignId) params.set("campaignId", filters.campaignId);
  if (filters.type) params.set("type", filters.type);
  return `/voice/get-voice-history?${params.toString()}`;
};

/* ─── sub-components ────────────────────────────────────────── */
const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: "default", icon: null };
  return (
    <Chip
      label={status || "Unknown"}
      color={cfg.color}
      size="small"
      icon={cfg.icon}
      sx={{ fontWeight: 600, fontSize: "0.7rem" }}
    />
  );
};

const TypeChip = ({ type }) => {
  const cfg = TYPE_CHIP_CONFIG[type] || { color: "default" };
  const label =
    type === "agent_notification"
      ? "Agent Notif."
      : type
      ? type.charAt(0).toUpperCase() + type.slice(1)
      : "Unknown";
  return (
    <Chip
      label={label}
      color={cfg.color}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem" }}
    />
  );
};

const CellValue = ({ col, row, payload }) => {
  const val = col.field(row, payload);
  const display = val ?? "—";

  if (col.chip) return <StatusChip status={val} />;
  if (col.typeChip) return <TypeChip type={val} />;
  if (col.truncate)
    return (
      <Tooltip title={display} placement="top">
        <Typography
          variant="caption"
          noWrap
          sx={{ display: "block", maxWidth: col.width - 20 }}
        >
          {display}
        </Typography>
      </Tooltip>
    );
  return (
    <Typography
      variant="caption"
      sx={{ fontFamily: col.mono ? "monospace" : "inherit" }}
    >
      {String(display)}
    </Typography>
  );
};

/* ─── main component ────────────────────────────────────────── */
const VoiceTransactionHistory = () => {
  const [committed, setCommitted] = useState({
    name: "",
    campaignId: "",
    type: "",
  });
  const [inputs, setInputs] = useState({ name: "", campaignId: "" });
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);

  // ── export state ──────────────────────────────────────────────
  const [requesting, setRequesting] = useState(false);
  const [exportJob, setExportJob] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const pollTimerRef = useRef(null);
  const pollStartRef = useRef(null);

  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useApiEp(buildQuery(page, committed));

  const rows = response?.data ?? [];
  const totalPages = response?.totalPages ?? 0;
  const totalItems = response?.totalItems ?? 0;

  // ── polling ───────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const triggerDownload = useCallback((url) => {
    const ts = new Date().toISOString().slice(0, 10);
    const link = document.createElement("a");
    link.href = url;
    link.download = `voice-history-${ts}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const pollJobStatus = useCallback(
    async (jobId) => {
      if (Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
        stopPolling();
        setExportJob((prev) => ({
          ...prev,
          status: "failed",
          error: "Export timed out. Please try again.",
        }));
        return;
      }

      try {
        const { data: job } = await axios.get(
          `${API_ENDPOINT}/voice/voice-history-export-status/${jobId}`,
          getHeader()
        );
        setExportJob(job);

        if (["done", "failed", "cancelled"].includes(job.status)) {
          stopPolling();
          if (job.status === "done" && job.fileUrl) {
            triggerDownload(job.fileUrl);
            setSnackbar({
              open: true,
              message: "Export ready — downloading now.",
            });
          }
        }
      } catch (err) {
        console.error("[voice-export] Poll error:", err);
      }
    },
    [stopPolling, triggerDownload]
  );

  const startPolling = useCallback(
    (jobId) => {
      stopPolling();
      pollStartRef.current = Date.now();
      pollJobStatus(jobId);
      pollTimerRef.current = setInterval(
        () => pollJobStatus(jobId),
        POLL_INTERVAL_MS
      );
    },
    [pollJobStatus, stopPolling]
  );

  // cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  // ── export handlers ───────────────────────────────────────────
  const handleExport = async () => {
    // If already done, re-download
    if (exportJob?.status === "done" && exportJob?.fileUrl) {
      triggerDownload(exportJob.fileUrl);
      return;
    }

    // If already in progress, just notify
    if (exportJob?.status === "pending" || exportJob?.status === "processing") {
      setSnackbar({
        open: true,
        message: "Your export is already in progress.",
      });
      return;
    }

    try {
      setRequesting(true);
      const params = new URLSearchParams();
      if (committed.name) params.set("name", committed.name);
      if (committed.campaignId) params.set("campaignId", committed.campaignId);
      if (committed.type) params.set("type", committed.type);

      const { data: job } = await axios.post(
        `${API_ENDPOINT}/voice/voice-history-export-request?${params.toString()}`,
        {},
        getHeader()
      );
      setExportJob(job);

      if (job.status === "done" && job.fileUrl) {
        // Rare: job was already cached and ready immediately
        triggerDownload(job.fileUrl);
        setSnackbar({ open: true, message: "Export ready — downloading now." });
      } else {
        startPolling(job.jobId);
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Export failed. Please try again." });
    } finally {
      setRequesting(false);
    }
  };

  const handleDismissBanner = async () => {
    stopPolling();
    if (exportJob?.jobId && exportJob?.status === "pending") {
      try {
        await axios.delete(
          `${API_ENDPOINT}/voice/voice-history-export-cancel/${exportJob.jobId}`,
          getHeader()
        );
      } catch (_) {}
    }
    setExportJob(null);
  };

  // ── filter handlers ───────────────────────────────────────────
  const handleSearch = () => {
    setPage(1);
    setCommitted((prev) => ({ ...inputs, type: prev.type }));
  };

  const handleTypeChange = (_, newType) => {
    const val = newType ?? "";
    setTypeFilter(val);
    setPage(1);
    setCommitted((prev) => ({ ...prev, type: val }));
    // Reset export state when filters change
    stopPolling();
    setExportJob(null);
  };

  const handleClear = () => {
    const empty = { name: "", campaignId: "", type: "" };
    setInputs({ name: "", campaignId: "" });
    setTypeFilter("");
    setCommitted(empty);
    setPage(1);
    stopPolling();
    setExportJob(null);
  };

  const anyFilter = inputs.name || inputs.campaignId || typeFilter;

  // ── derived export UI state ───────────────────────────────────
  const exportInProgress =
    exportJob?.status === "pending" || exportJob?.status === "processing";

  const exportButtonLabel = exportInProgress
    ? "Processing…"
    : exportJob?.status === "done"
    ? "Download Again"
    : "Export CSV";

  /* ── render ── */
  return (
    <Box sx={{ p: 3 }}>
      {/* Header row */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Voice Transaction History
          </Typography>
          {totalItems > 0 && (
            <Typography variant="caption" color="text.secondary">
              {totalItems.toLocaleString()} records
              {(committed.name || committed.campaignId || committed.type) &&
                " (filtered)"}
            </Typography>
          )}
        </Box>

        <Tooltip
          title={
            exportInProgress
              ? "Export is being processed in the background"
              : exportJob?.status === "done"
              ? "Click to download your report again"
              : "Export all voice records to CSV"
          }
        >
          <span>
            <LoadingButton
              variant="outlined"
              size="small"
              startIcon={
                exportJob?.status === "done" ? (
                  <DownloadDoneIcon />
                ) : (
                  <FileDownloadIcon />
                )
              }
              color={exportJob?.status === "done" ? "success" : "primary"}
              loading={requesting || exportInProgress}
              onClick={handleExport}
              sx={{ whiteSpace: "nowrap" }}
            >
              {exportButtonLabel}
            </LoadingButton>
          </span>
        </Tooltip>
      </Stack>

      {/* Export status banner */}
      <ExportStatusBanner
        jobState={exportJob}
        onDismiss={handleDismissBanner}
        onDownload={triggerDownload}
      />

      {/* Filter bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
        >
          {/* Type toggle — applied instantly */}
          <ToggleButtonGroup
            value={typeFilter}
            exclusive
            onChange={handleTypeChange}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            {TYPE_OPTIONS.map((opt) => (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                sx={{ px: 2, textTransform: "none", fontSize: "0.8rem" }}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <TextField
            size="small"
            label="Customer ID"
            placeholder="Search customer…"
            value={inputs.name}
            onChange={(e) => setInputs((s) => ({ ...s, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />

          <TextField
            size="small"
            label="Campaign ID"
            placeholder="e.g. 940725"
            value={inputs.campaignId}
            onChange={(e) =>
              setInputs((s) => ({ ...s, campaignId: e.target.value }))
            }
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 180 }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            sx={{ px: 3 }}
          >
            Search
          </Button>

          {anyFilter && (
            <Tooltip title="Clear all filters">
              <IconButton size="small" onClick={handleClear}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Paper>

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <Alert severity="error">
          {error?.message ?? "Failed to load voice history."}
        </Alert>
      )}

      {/* Table */}
      {!isLoading && !isError && (
        <>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) => (
                    <TableCell
                      key={col.label}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        whiteSpace: "nowrap",
                        minWidth: col.width,
                        bgcolor: "grey.50",
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMNS.length}
                      align="center"
                      sx={{ py: 6 }}
                    >
                      <Typography color="text.secondary" variant="body2">
                        No records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const p = row.payload ?? {};
                    return (
                      <TableRow key={row._id} hover>
                        {COLUMNS.map((col) => (
                          <TableCell key={col.label} sx={{ py: 0.8 }}>
                            <CellValue col={col} row={row} payload={p} />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default VoiceTransactionHistory;
